import { AppNav } from '@/components/AppNav';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useGetPricingQuery } from '@/generated/graphql';
import { useMonetizationEnabled } from '@/hooks/useMonetizationEnabled';
import { formatPrice } from '@/utils/formatPrice';
import { Link } from 'react-router-dom';
import { SUPPORT_ROUTE } from './consts';
import { Footer } from '@/components/Footer';
import { useTranslation, FormattedMessage } from '@/i18n';

export function Pricing() {
  const { t } = useTranslation();
  const { data } = useGetPricingQuery();
  const { monetizationEnabled } = useMonetizationEnabled();

  if (!monetizationEnabled) {
    return (
      <div data-name="PRICING" className="flex min-h-screen flex-col">
        <AppNav />
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
          <h1 className="text-4xl font-bold">{t('pricing.title')}</h1>
          <p className="text-center text-xl text-muted-foreground">
            Happi Screen is free right now — no Event Pass or subscription
            needed.
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-name="PRICING" className="flex min-h-screen flex-col">
      <AppNav />
      <div className="flex flex-1 flex-col items-center justify-center gap-8 px-4 md:px-0">
        <h1 className="text-4xl font-bold">
          {t('pricing.title')}:{' '}
          <span className="text-muted-foreground">{t('pricing.subtitle')}</span>
        </h1>
        <div
          data-name="PRICING_CARDS"
          className="flex flex-col gap-4 md:flex-row"
        >
          <Card className="border-constructive bg-constructive/80">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-6xl">🎫</span>
                  <span className="text-3xl font-bold">
                    {t('pricing.eventPass.title')}
                  </span>
                </div>
                {data?.pricing?.eventPass ? (
                  <span className="font-epic text-5xl text-white">
                    {formatPrice(data.pricing.eventPass)}
                  </span>
                ) : null}
              </CardTitle>
              <CardDescription className="text-xl italic text-foreground/80">
                <FormattedMessage
                  id="pricing.eventPass.description"
                  values={{ strong: (chunks) => <strong>{chunks}</strong> }}
                />
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p>{t('pricing.eventPass.details')}</p>
            </CardContent>
          </Card>
          <Card className="border-epic bg-epic/80">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="text-6xl">🔃</span>
                  <span className="text-3xl font-bold">
                    {t('pricing.subscription.title')}
                  </span>
                </div>
                {data?.pricing?.subscription ? (
                  <span className="font-epic text-5xl text-white">
                    {formatPrice(data.pricing.subscription)}
                    {t('pricing.subscription.perMonth')}
                  </span>
                ) : null}
              </CardTitle>
              <CardDescription className="text-xl italic text-foreground/80">
                <FormattedMessage
                  id="pricing.subscription.description"
                  values={{ strong: (chunks) => <strong>{chunks}</strong> }}
                />
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p>{t('pricing.subscription.details')}</p>
            </CardContent>
          </Card>
        </div>
        <p className="text-center text-xl italic text-foreground/80">
          <FormattedMessage
            id="pricing.footer"
            values={{
              link: (chunks) => <Link to={SUPPORT_ROUTE}>{chunks}</Link>,
            }}
          />
        </p>
      </div>
      <Footer />
    </div>
  );
}
